import {observer} from 'mobx-react-lite'
import {useCallback, useEffect, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {Button} from 'components/generic'
import {Heading} from 'components/template/Heading'
import {Spinner} from 'components/template/Spinner'
import {CouponQueueDetailDto, CouponQueueService} from 'rest'
import {PaginatedResult} from 'rest/types/PaginatedResult'
import DraggableRow, {RowRenderer} from 'components/generic/DraggableRow'
import update from 'immutability-helper'
import {toast} from 'react-hot-toast'
import {CouponCard} from 'components/template/CouponCard'
import MerchantStatus from 'components/MerchantStatus'

export const Coupons = observer(() => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [updatingChanges, setUpdatingChanges] = useState(false)
  const [pendingSortChanges, setPendingSortChanges] = useState(false)

  const [coupons, setCoupons] =
    useState<PaginatedResult<CouponQueueDetailDto>>()

  const [sortedItems, setSortedItems] = useState<CouponQueueDetailDto[]>([])

  let merchant = JSON.parse(localStorage.getItem('user'))?.store
  let transaction = JSON.parse(localStorage.getItem('user'))?.coupon_center_txn

  useEffect(() => {
    if (!merchant) navigate('/my-store')
    ;(async () => {
      setLoading(true)
      const coupons =
        await CouponQueueService.getApiCouponQueueGetByMerchantId()
      setCoupons(coupons)
      setLoading(false)
      if (!coupons?.results?.length) navigate('/coupons/add')
    })()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (coupons?.results?.length > 0) {
      setSortedItems(coupons.results)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupons?.results?.length])

  const isActive = !!transaction

  const moveDraggableRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setSortedItems(order =>
        update(order, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, order[dragIndex]],
          ],
        })
      )

      setPendingSortChanges(true)
    },

    []
  )

  const renderRow: RowRenderer<CouponQueueDetailDto> = row => (
    <CouponCard coupon={row} />
  )

  const saveSortChanges = async () => {
    setUpdatingChanges(true)
    const response = await CouponQueueService.updateSortOrder({
      items: sortedItems.map((item, index) => ({
        id: String(item.id),
        sort_number: String(index + 1),
      })),
    })

    setPendingSortChanges(false)
    setUpdatingChanges(false)

    if (response.success) {
      toast.success('Your changes were saved successfully')
    }
  }

  return (
    <div>
      <Heading title="MY COUPONS">
        {isActive && coupons?.count < 10 && (
          <Link to="/coupons/add" className="button add w-32">
            ADD COUPON
          </Link>
        )}
        {!isActive || coupons?.count === 10 ? (
          <Button onClick={() => null} className="button add grayscale w-32">
            ADD COUPON
          </Button>
        ) : null}
      </Heading>
      <div className="flex flex-col min-h-screen">
        <MerchantStatus loading={loading} />
        <div className="flex items-center">
          {loading ? (
            <Spinner className="py-8" />
          ) : (
            <div className="flex flex-col items-start flex-1">
              {coupons?.count === 0 && (
                <h1 className="flex justify-between w-full py-4 text-xl font-semibold leading-8 tracking-tight">
                  CREATE COUPON FOR YOUR STORE PAGE{' '}
                  <span className="text-gray-400">{coupons?.count} COUPON</span>
                </h1>
              )}
              <div className="my-8">
                <div className="mb-2 flex items-center justify-between text-sm text-gray-500 italic">
                  {sortedItems?.length > 1 && (
                    <div className="flex gap-2 items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4 mr-1 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 8h16M4 16h16"
                        />
                      </svg>

                      <span>Drag and drop to rearrange coupons</span>
                    </div>
                  )}
                  {pendingSortChanges && (
                    <button onClick={saveSortChanges} className="button edit">
                      {' '}
                      {updatingChanges ? 'Saving changes...' : 'Save changes'}
                    </button>
                  )}
                </div>

                {sortedItems.map((item, idx) => (
                  <DraggableRow
                    key={item.id}
                    index={idx}
                    id={item.id}
                    row={item}
                    rowClassName="border-1 border-gray-300"
                    renderRow={renderRow as RowRenderer<Record<string, any>>}
                    moveDraggableRow={moveDraggableRow}
                  />
                ))}
              </div>
              <div className="flex justify-end w-full">
                <a
                  href="mailto:help@promocodess.com"
                  className="button formSecondary w-36 mt-6">
                  NEED HELP?
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
})
